/**
 * PlatformTrendsCharts — shared trends charts for /superadmin/analytics
 * (decision 2-A, 2026-09-25). Parses GET /api/analytics/trends and renders
 * daily activity + module-usage charts, mirroring SuperAdminUsage styling.
 */
import { useMemo } from 'react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Line, Legend,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

const asNum = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0);
const asStr = (v: unknown): string => (v == null ? '' : String(v));

export interface TrendsDailyPoint { date: string; total: number }
export interface TrendsEventSeries { event: string; count: number; daily: Record<string, number> }
export interface TrendsActiveUserPoint { date: string; users: number }
export interface TrendsModuleViews { module: string; views: number }

export interface TrendsData {
  days: number;
  daily: TrendsDailyPoint[];
  byEvent: TrendsEventSeries[];
  activeUsers: TrendsActiveUserPoint[];
  pageViewsByModule: TrendsModuleViews[];
  total: number;
}

export const MODULE_LABELS: Record<string, string> = {
  access: 'Accesos', amenities: 'Amenidades', announcements: 'Anuncios', complaints: 'Quejas y sugerencias',
  payments: 'Pagos', reservations: 'Reservas', visitors: 'Visitas', tasks: 'Tareas', wall: 'Muro',
  voting: 'Votaciones', expenses: 'Gastos', marketplace: 'Marketplace', documents: 'Documentos',
  page_view: 'Navegación (vistas)',
};
export const moduleLabel = (m: string): string => MODULE_LABELS[m] ?? m.replace(/_/g, ' ');

export const toTrends = (value: unknown): TrendsData | null => {
  const root = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const data = (root.data ?? root) as Record<string, unknown>;
  if (!data || typeof data !== 'object') return null;
  const daily = Array.isArray(data.daily) ? (data.daily as Record<string, unknown>[]) : [];
  const byEvent = Array.isArray(data.byEvent) ? (data.byEvent as Record<string, unknown>[]) : [];
  const activeUsers = Array.isArray(data.activeUsers) ? (data.activeUsers as Record<string, unknown>[]) : [];
  const pv = Array.isArray(data.pageViewsByModule) ? (data.pageViewsByModule as Record<string, unknown>[]) : [];
  return {
    days: asNum(data.days) || 7,
    daily: daily.map((d) => ({ date: asStr(d.date), total: asNum(d.total) })),
    byEvent: byEvent.map((e) => ({ event: asStr(e.event), count: asNum(e.count), daily: (e.daily && typeof e.daily === 'object') ? e.daily as Record<string, number> : {} })),
    activeUsers: activeUsers.map((a) => ({ date: asStr(a.date), users: asNum(a.users) })),
    pageViewsByModule: pv.map((p) => ({ module: asStr(p.module), views: asNum(p.views) })),
    total: asNum(data.total),
  };
};

/** Last `days` UTC dates, ascending — keeps gaps as zero so charts are continuous. */
function dayAxis(days: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i));
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}
const shortDate = (iso: string): string => iso.slice(5); // MM-DD

/** Module of a feature event: first snake token, e.g. complaints_complaint_created -> complaints. */
const eventModule = (event: string): string => event.split('_')[0] || 'otros';

const tooltipStyle = { background: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: 12, color: '#fff' } as const;

export const TrendsCharts = ({ data, days }: { data: TrendsData; days: number }) => {
  const axis = useMemo(() => dayAxis(days), [days]);

  const dailySeries = useMemo(() => {
    const totalByDate = new Map(data.daily.map((d) => [d.date, d.total]));
    const usersByDate = new Map(data.activeUsers.map((a) => [a.date, a.users]));
    return axis.map((date) => ({ date: shortDate(date), eventos: totalByDate.get(date) ?? 0, usuarios: usersByDate.get(date) ?? 0 }));
  }, [data, axis]);

  /** Top feature modules by creation/action events (page_view excluded). */
  const moduleUsage = useMemo(() => {
    const byModule = new Map<string, { eventos: number; vistos: number }>();
    for (const ev of data.byEvent) {
      if (ev.event === 'page_view') continue;
      const m = eventModule(ev.event);
      const row = byModule.get(m) ?? { eventos: 0, vistos: 0 };
      row.eventos += ev.count;
      byModule.set(m, row);
    }
    const views = new Map(data.pageViewsByModule.map((p) => [p.module, p.views]));
    return [...byModule.entries()]
      .map(([module, row]) => ({ module: moduleLabel(module), eventos: row.eventos, vistas: views.get(module) ?? 0 }))
      .sort((a, b) => b.eventos - a.eventos)
      .slice(0, 10);
  }, [data]);

  const hasAnyData = data.total > 0 || data.daily.some((d) => d.total > 0) || data.pageViewsByModule.length > 0;

  if (!hasAnyData) {
    return (
      <div className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-6 text-gray-400 text-sm">
        Sin datos de actividad para los últimos {days} días.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-5">
        <h2 className="text-lg font-bold text-white mb-1">Actividad diaria</h2>
        <p className="text-gray-500 text-xs mb-4">Eventos totales por día y usuarios activos distintos</p>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={dailySeries}>
            <defs>
              <linearGradient id="platformTrendEvents" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#36CCD8" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#36CCD8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" />
            <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
            <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ paddingTop: 10 }} />
            <Area type="monotone" dataKey="eventos" stroke="#36CCD8" fill="url(#platformTrendEvents)" strokeWidth={2} name="Eventos" />
            <Line type="monotone" dataKey="usuarios" stroke="#F59E0B" strokeWidth={2} dot={false} name="Usuarios activos" />
          </AreaChart>
        </ResponsiveContainer>
      </section>

      <section className="bg-[#2a2a2a] rounded-xl border border-[#3a3a3a] p-5">
        <h2 className="text-lg font-bold text-white mb-1">Uso de módulos</h2>
        <p className="text-gray-500 text-xs mb-4">Acciones de creación/uso por módulo (top 10)</p>
        {moduleUsage.length === 0 ? <p className="text-gray-400 text-sm">Sin acciones registradas en este rango.</p> : (
          <ResponsiveContainer width="100%" height={Math.max(200, moduleUsage.length * 34)}>
            <BarChart data={moduleUsage} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="module" width={150} tick={{ fill: '#d1d5db', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="eventos" name="Acciones" fill="#36CCD8" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </section>
    </div>
  );
};
